<script setup lang="ts">
import type { CartLine } from '~~/shared/types/store'

const props = defineProps<{
  line: CartLine
  updating?: boolean
  removing?: boolean
  stockLimitReached?: boolean
  /**
   * Pozycja z sekcji „Wyprzedane" — nie da się jej kupić. Zostaje widoczna
   * (wyszarzona, bez steppera), żeby klient wiedział, co się stało z produktem,
   * a nie szukał go po koszyku, z którego zniknął.
   */
  unavailable?: boolean
  restoring?: boolean
}>()

const emit = defineEmits<{
  updateQuantity: [quantity: number]
  remove: []
  restore: []
}>()

const { formatPrice } = useMoney()
const isBusy = computed(() => props.updating || props.removing || props.restoring)
const unavailableLabel = computed(() => {
  // Rozróżnienie ma znaczenie dla klienta: „wyprzedane" wróci na stan, „wycofane" nie.
  if (props.line.unavailableReason === 'inactive' || props.line.unavailableReason === 'not_found') {
    return 'Produkt wycofany ze sprzedaży'
  }
  return 'Wyprzedane'
})
const meta = computed(() => [
  lineHasValue(props.line.brand) ? `Brand: ${props.line.brand}` : '',
  lineHasValue(props.line.color) ? `Kolor: ${props.line.color}` : '',
  lineHasValue(props.line.size) ? `Rozmiar: ${props.line.size}` : '',
].filter(Boolean))

function lineHasValue(value: string) {
  return Boolean(value?.trim())
}
</script>

<template>
  <article
    class="cart-line"
    :class="{
      'cart-line--busy': updating || restoring,
      'cart-line--removing': removing,
      'cart-line--unavailable': unavailable,
    }"
  >
    <NuxtLink :to="`/products/${line.productId}-${line.productSlug}`" class="cart-line__img-wrap">
      <ProductImage
        :src="line.thumbnail"
        :alt="line.name"
        width="200"
        height="266"
        format="webp"
        class="cart-line__img"
      />
    </NuxtLink>

    <div class="cart-line__info">
      <p class="cart-line__brand">{{ line.brand }}</p>
      <NuxtLink :to="`/products/${line.productId}-${line.productSlug}`" class="cart-line__name">{{ line.name }}</NuxtLink>
      <p v-for="item in meta" :key="item" class="cart-line__meta">{{ item }}</p>

      <!-- Sekcja „Wyprzedane": zamiast steppera powód i — gdy produkt wrócił
           na stan — jawna akcja przywrócenia. Przywracanie automatyczne
           podniosłoby kwotę do zapłaty bez decyzji klienta. -->
      <template v-if="unavailable">
        <p class="cart-line__unavailable" role="status">{{ unavailableLabel }}</p>
        <button
          v-if="line.canRestore"
          type="button"
          class="cart-line__restore"
          :disabled="isBusy"
          @click="emit('restore')"
        >
          {{ restoring ? 'Przywracanie…' : 'Przywróć do koszyka' }}
        </button>
      </template>

      <div v-else class="cart-line__qty-wrap">
        <div class="cart-line__stepper">
          <button
            type="button"
            class="cart-line__stepper-btn"
            :disabled="isBusy || line.quantity <= 1"
            aria-label="Zmniejsz ilość"
            @click="emit('updateQuantity', line.quantity - 1)"
          >−</button>
          <span class="cart-line__stepper-val">{{ line.quantity }}</span>
          <button
            type="button"
            class="cart-line__stepper-btn"
            :disabled="isBusy || stockLimitReached"
            aria-label="Zwiększ ilość"
            @click="emit('updateQuantity', line.quantity + 1)"
          >+</button>
        </div>
        <p v-if="stockLimitReached" class="cart-line__stock-limit">Więcej nie mamy — to ostatnie sztuki</p>
      </div>
    </div>

    <div class="cart-line__right">
      <button
        type="button"
        class="cart-line__remove"
        :disabled="isBusy"
        :aria-label="unavailable ? 'Usuń wyprzedany produkt z koszyka' : 'Usuń z koszyka'"
        @click="emit('remove')"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6M14 11v6" />
          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
        </svg>
      </button>
      <p class="cart-line__price">{{ formatPrice(line.price * line.quantity) }}</p>
      <p v-if="line.compareAtPrice" class="cart-line__compare">{{ formatPrice(line.compareAtPrice * line.quantity) }}</p>
    </div>

    <div v-if="removing" class="cart-line__removing" role="status" aria-live="polite">
      <span class="cart-line__removing-spinner" aria-hidden="true" />
      <span class="cart-line__removing-label">Usuwanie…</span>
    </div>
  </article>
</template>

<style scoped>
.cart-line {
  position: relative;
  display: grid;
  grid-template-columns: 150px 1fr auto;
  gap: 16px;
  padding: 20px 0;
  border-bottom: 1px solid var(--color-border);
  align-items: start;
  transition: opacity var(--transition-fast);
}

.cart-line--busy {
  opacity: 0.5;
  pointer-events: none;
}

/* Removal in progress: animated graying of the line content, with a
   crisp spinner overlay layered on top so the indicator stays readable. */
.cart-line--removing {
  pointer-events: none;
}

.cart-line--removing > .cart-line__img-wrap,
.cart-line--removing > .cart-line__info,
.cart-line--removing > .cart-line__right {
  filter: grayscale(1);
  animation: cart-line-removing-pulse 1.2s ease-in-out infinite;
}

@keyframes cart-line-removing-pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 0.2; }
}

.cart-line__removing {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.cart-line__removing-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--color-border-md);
  border-top-color: var(--color-black);
  border-radius: 50%;
  animation: cart-line-spin 0.7s linear infinite;
}

@keyframes cart-line-spin {
  to { transform: rotate(360deg); }
}

.cart-line__removing-label {
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
}

@media (prefers-reduced-motion: reduce) {
  .cart-line--removing > .cart-line__img-wrap,
  .cart-line--removing > .cart-line__info,
  .cart-line--removing > .cart-line__right {
    animation: none;
    opacity: 0.4;
  }

  .cart-line__removing-spinner {
    animation: none;
  }
}

/* Sekcja „Wyprzedane".
   Wyszarzenie zdjęcia + wyciszenie tekstu, bez przekreślenia: w tym projekcie
   `line-through` znaczy „ten wariant nie istnieje" (ProductSizePickerModal),
   a tu produkt istnieje i może wrócić na stan. Nie używamy `pointer-events: none`
   — usunięcie i przywrócenie muszą pozostać klikalne. */
.cart-line--unavailable .cart-line__img {
  filter: grayscale(1);
  opacity: 0.55;
}

.cart-line--unavailable .cart-line__name,
.cart-line--unavailable .cart-line__price {
  color: var(--color-muted);
}

.cart-line__unavailable {
  margin: 8px 0 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-sale);
}

.cart-line__restore {
  margin-top: 8px;
  align-self: flex-start;
  background: none;
  border: 1px solid var(--color-border-md);
  cursor: pointer;
  padding: 6px 12px;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  transition: background var(--transition-fast);
}

.cart-line__restore:hover:not(:disabled) {
  background: var(--color-surface);
}

.cart-line__restore:disabled {
  color: var(--color-muted);
  cursor: not-allowed;
}

/* Image */
.cart-line__img-wrap {
  display: block;
  overflow: hidden;
}

.cart-line__img {
  width: 100%;
  height: 180px;
  object-fit: cover;
  object-position: top center;
  display: block;
}

/* Info */
.cart-line__info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 2px;
}

.cart-line__brand {
  margin: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1;
}

.cart-line__name {
  font-size: 0.9375rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1.3;
  text-transform: uppercase;
}

.cart-line__meta {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1;
}

.cart-line__qty-wrap {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.cart-line__stock-limit {
  margin: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: var(--color-sale);
}

.cart-line__stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--color-border-md);
  height: 32px;
}

.cart-line__stepper-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 100%;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: var(--color-black);
  padding: 0;
  transition: background var(--transition-fast);
  user-select: none;
}

.cart-line__stepper-btn:hover:not(:disabled) {
  background: var(--color-surface);
}

.cart-line__stepper-btn:disabled {
  color: var(--color-muted);
  cursor: not-allowed;
}

.cart-line__stepper-val {
  min-width: 28px;
  text-align: center;
  font-size: 0.875rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  border-left: 1px solid var(--color-border-md);
  border-right: 1px solid var(--color-border-md);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Right */
.cart-line__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  padding-top: 2px;
}

.cart-line__remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-muted);
  padding: 0;
  display: flex;
  transition: color var(--transition-fast);
}

.cart-line__remove:hover {
  color: var(--color-black);
}

.cart-line__price {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-sale);
  line-height: 1;
}

.cart-line__compare {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  text-decoration: line-through;
}

@media (max-width: 640px) {
  .cart-line {
    grid-template-columns: 80px 1fr auto;
    gap: 12px;
  }

  .cart-line__img {
    height: 107px;
  }
}
</style>
